/**
 * Date Logic — Frontend date/time conditional logic evaluation.
 *
 * Ports the gform_is_value_match filter from GP Conditional Logic Dates (GPCLD)
 * into TypeScript. Handles:
 *   - Date fields (HTML5, dropdown, datepicker) vs static dates, {today}, wildcards, day-of-week
 *   - Time fields vs static times
 *   - _gpacl_current_date / _gpcld_current_date sentinel field IDs
 *   - _gpacl_current_time / _gpcld_current_time sentinel field IDs
 *   - Visitor timezone support (GPCLD_USE_VISITOR_TIMEZONE_{formId})
 *   - gpcldBaseDate rule property for time anchoring
 *
 * @package
 */

import type { GPACLRule } from '../typings/shared';
import { registerModule } from './module-registry';
import { isAnySentinel } from '../shared/sentinels';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Left-pad a number with zeros to a given width.
 * Avoids String.prototype.padStart which requires es2017.string lib.
 *
 * @param n     The number to pad.
 * @param width The desired total width.
 * @return Zero-padded string.
 */
function zeroPad(n: number, width: number): string {
	const s = String(n);
	return s.length >= width
		? s
		: new Array(width - s.length + 1).join('0') + s;
}

// -------------------------------------------------------------------------
// Sentinel ID helpers
// -------------------------------------------------------------------------

/**
 * Check if a fieldId refers to the current-date sentinel (new or BC name).
 * @param fieldId
 */
function isCurrentDateRule(fieldId: string | number): boolean {
	return (
		fieldId === '_gpacl_current_date' || fieldId === '_gpcld_current_date'
	);
}

/**
 * Check if a fieldId refers to the current-time sentinel (new or BC name).
 * @param fieldId
 */
function isCurrentTimeRule(fieldId: string | number): boolean {
	return (
		fieldId === '_gpacl_current_time' || fieldId === '_gpcld_current_time'
	);
}

// -------------------------------------------------------------------------
// Date field helpers
// -------------------------------------------------------------------------

interface DateFieldFormat {
	sep: string;
	format: string; // e.g. 'mdy', 'dmy', 'ymd'
}

/**
 * Read the date format from a field's .gfield container CSS classes.
 * Reads both new gpacl-date-format-* and BC gcldf-date-format-* classes.
 *
 * @param $field jQuery object for the field's input element.
 * @return DateFieldFormat with sep and format.
 */
function gpGetDateFieldFormat($field: JQuery): DateFieldFormat {
	const $gfield = $field.closest('.gfield');
	const classes = $gfield.attr('class') || '';

	// Try new class name first, then BC.
	let match = classes.match(/gpacl-date-format-([\w-]+)/);
	if (!match) {
		match = classes.match(/gcldf-date-format-([\w-]+)/);
	}

	if (!match) {
		return { sep: '/', format: 'mdy' };
	}

	const raw = match[1]; // e.g. 'mdy', 'dmy', 'ymd', 'mdy_dash', 'mdy_dot'
	let sep: string;
	if (raw.includes('_dash')) {
		sep = '-';
	} else if (raw.includes('_dot')) {
		sep = '.';
	} else {
		sep = '/';
	}
	const format = raw.replace(/_dash|_dot/, '');

	return { sep, format };
}

interface DateParts {
	month: number; // 1-indexed
	day: number;
	year: number;
}

/**
 * Read the date value from a date field input.
 * Handles three input types: HTML5 date, dropdown/fields, datepicker text.
 *
 * @param $field      jQuery object for the field's input element.
 * @param fieldFormat Optional pre-computed format.
 * @return DateParts or null if incomplete.
 */
function gpGetDateFieldDate(
	$field: JQuery,
	fieldFormat?: DateFieldFormat
): DateParts | null {
	const format = fieldFormat || gpGetDateFieldFormat($field);
	const $gfield = $field.closest('.gfield');

	// HTML5 date input (type="date") — value is YYYY-MM-DD.
	const $html5 = $gfield.find('input[type="date"]');
	if ($html5.length) {
		const val = $html5.val() as string;
		if (!val) {
			return null;
		}
		const parts = val.split('-');
		if (parts.length !== 3) {
			return null;
		}
		return {
			month: parseInt(parts[1], 10),
			day: parseInt(parts[2], 10),
			year: parseInt(parts[0], 10),
		};
	}

	// Dropdown / multi-field date inputs.
	// The container divs have the class; find the select/input inside.
	const $monthDropdown = $gfield.find(
		'.gfield_date_dropdown_month select, .gfield_date_dropdown_month input, .gfield_date_month select, .gfield_date_month input'
	);
	const $dayDropdown = $gfield.find(
		'.gfield_date_dropdown_day select, .gfield_date_dropdown_day input, .gfield_date_day select, .gfield_date_day input'
	);
	const $yearDropdown = $gfield.find(
		'.gfield_date_dropdown_year select, .gfield_date_dropdown_year input, .gfield_date_year select, .gfield_date_year input'
	);

	if ($monthDropdown.length && $dayDropdown.length && $yearDropdown.length) {
		const month = parseInt($monthDropdown.val() as string, 10);
		const day = parseInt($dayDropdown.val() as string, 10);
		const year = parseInt($yearDropdown.val() as string, 10);
		if (!month || !day || !year) {
			return null;
		}
		return { month, day, year };
	}

	// Datepicker / text input — split by separator.
	const val = ($field.val() as string) || '';
	if (!val) {
		return null;
	}

	const parts = val.split(format.sep);
	if (parts.length < 3) {
		return null;
	}

	let month: number, day: number, year: number;

	switch (format.format) {
		case 'dmy':
			day = parseInt(parts[0], 10);
			month = parseInt(parts[1], 10);
			year = parseInt(parts[2], 10);
			break;
		case 'ymd':
			year = parseInt(parts[0], 10);
			month = parseInt(parts[1], 10);
			day = parseInt(parts[2], 10);
			break;
		default: // mdy
			month = parseInt(parts[0], 10);
			day = parseInt(parts[1], 10);
			year = parseInt(parts[2], 10);
	}

	if (!month || !day || !year) {
		return null;
	}

	return { month, day, year };
}

/**
 * Get a Date object from a date field input.
 *
 * @param $field jQuery object for the field's input element.
 * @return Date object or null if incomplete.
 */
function gpGetDateFieldDateObject($field: JQuery): Date | null {
	const parts = gpGetDateFieldDate($field);
	if (!parts) {
		return null;
	}

	const d = new Date(parts.year, parts.month - 1, parts.day);
	if (isNaN(d.getTime())) {
		return null;
	}

	return d;
}

/**
 * Get a Unix timestamp (seconds) from a date field input.
 *
 * @param $field jQuery object for the field's input element.
 * @return Unix timestamp in seconds, or false if incomplete.
 */
function gpGetDateFieldTimestamp(
	$field: JQuery,
	formId: number // eslint-disable-line @typescript-eslint/no-unused-vars
): number | false {
	const parts = gpGetDateFieldDate($field);
	if (!parts) {
		return false;
	}

	// Use UTC midnight to avoid local TZ shifting the date.
	// Both PHP (strtotime in UTC) and JS (Date.UTC) produce the same
	// UTC midnight timestamp for a given calendar date. No server TZ
	// adjustment is needed here — that's only for current-date/time
	// sentinels where we need to know "what date is it on the server."
	const utcMs = Date.UTC(parts.year, parts.month - 1, parts.day);
	return utcMs / 1000;
}

// -------------------------------------------------------------------------
// Date string / time string parsers
// -------------------------------------------------------------------------

/**
 * Parse a date string (mm/dd/yyyy or other formats) into a Date object.
 *
 * @param dateString  The date string to parse.
 * @param fieldFormat Optional format hint.
 * @return Date object or null.
 */
function getDateFromDateString(
	dateString: string,
	fieldFormat?: DateFieldFormat
): Date | null {
	const format = fieldFormat || { sep: '/', format: 'mdy' };
	const parts = dateString.split(format.sep);
	if (parts.length < 3) {
		return null;
	}

	let month: number, day: number, year: number;

	switch (format.format) {
		case 'dmy':
			day = parseInt(parts[0], 10);
			month = parseInt(parts[1], 10);
			year = parseInt(parts[2], 10);
			break;
		case 'ymd':
			year = parseInt(parts[0], 10);
			month = parseInt(parts[1], 10);
			day = parseInt(parts[2], 10);
			break;
		default: // mdy
			month = parseInt(parts[0], 10);
			day = parseInt(parts[1], 10);
			year = parseInt(parts[2], 10);
	}

	if (!month || !day || !year) {
		return null;
	}

	// Use UTC midnight to match gpGetDateFieldTimestamp which also uses Date.UTC.
	const utcMs = Date.UTC(year, month - 1, day);
	const d = new Date(utcMs);
	if (isNaN(d.getTime())) {
		return null;
	}

	return d;
}

/**
 * Parse a time string (e.g. "09:00pm", "14:30") into a Date object
 * with today's date and the given time.
 *
 * @param timeString The time string to parse.
 * @return Date object or null.
 */
function getDateFromTimeString(timeString: string): Date | null {
	if (!timeString) {
		return null;
	}

	// Normalize GF merge tag format: "09, 00, pm" → "09:00pm"
	// GFMergeTag.getMergeTagValue() for time fields returns sub-input
	// values joined with ", " (comma-space).
	let normalized = timeString.trim();
	const csvMatch = normalized.match(/^(\d{1,2}),\s*(\d{1,2}),\s*(am|pm)$/i);
	if (csvMatch) {
		const mm = csvMatch[2].length === 1 ? '0' + csvMatch[2] : csvMatch[2];
		normalized = csvMatch[1] + ':' + mm + csvMatch[3];
	}

	// Normalize: "09:00pm" → parse hour, minute, ampm.
	const match = normalized.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
	if (!match) {
		return null;
	}

	let hour = parseInt(match[1], 10);
	const minute = parseInt(match[2], 10);
	const ampm = (match[3] || '').toLowerCase();

	if (ampm === 'pm' && hour < 12) {
		hour += 12;
	}
	if (ampm === 'am' && hour === 12) {
		hour = 0;
	}

	const d = new Date();
	d.setHours(hour, minute, 0, 0);

	return d;
}

/**
 * Check if a value is a negative Unix timestamp string (pre-1970 date).
 *
 * @param value The value to check.
 * @return true if value is a negative integer string.
 */
function isNegativeUnixTimestamp(value: string): boolean {
	return /^-\d+$/.test(value);
}

/**
 * Parse a wildcard date pattern like "05-*-*" (month/day/year) into numeric parts.
 * Supports common separators: "/", "-", ".".
 *
 * @param value The wildcard date pattern to parse.
 * @return Object with month/day/year properties (undefined if wildcard) or null if invalid.
 */
function parseWildcardDatePattern(value: string): {
	month?: number;
	day?: number;
	year?: number;
} | null {
	if (typeof value !== 'string' || !value.includes('*')) {
		return null;
	}

	const parts = value.split(/[\/\-.]/);
	if (parts.length !== 3) {
		return null;
	}

	const [m, d, y] = parts;
	const month = m !== '*' ? parseInt(m, 10) : undefined;
	const day = d !== '*' ? parseInt(d, 10) : undefined;
	const year = y !== '*' ? parseInt(y, 10) : undefined;

	if (month !== undefined && (isNaN(month) || month < 1 || month > 12)) {
		return null;
	}
	if (day !== undefined && (isNaN(day) || day < 1 || day > 31)) {
		return null;
	}

	if (year !== undefined && isNaN(year)) {
		return null;
	}

	return { month, day, year };
}

// -------------------------------------------------------------------------
// Core evaluation
// -------------------------------------------------------------------------

/**
 * Evaluate a date/time conditional logic rule.
 *
 * @param isMatch The current match result from GF.
 * @param formId  The form ID.
 * @param rule    The conditional logic rule.
 * @return Modified match result.
 */
function evaluateDateRule(
	isMatch: boolean,
	formId: number,
	rule: GPACLRule & { gpcldBaseDate?: string | number }
): boolean {
	const fieldId = rule.fieldId;

	const isCurrentTime = isCurrentTimeRule(fieldId);
	const isCurrentDate = isCurrentDateRule(fieldId);

	// Detect date/time fields by CSS class on their .gfield container.
	// NOTE: GF time fields have no element with id="input_{formId}_{fieldId}" —
	// they render three sub-inputs (_1 hour, _2 minute, _3 AM/PM) instead.
	// Use the .gfield container directly (same as handles()) so isTimeField
	// detection works. $sourceInput is only needed for date fields (datepicker)
	// and is resolved lazily inside the isDateField branch below.
	const $gfield = jQuery(`#field_${formId}_${fieldId}`);
	const gfieldClasses = $gfield.attr('class') || '';

	const isTimeField =
		gfieldClasses.includes('gpacl-date-field-time') ||
		gfieldClasses.includes('gcldf-field-time');
	const isDateField =
		gfieldClasses.includes('gpacl-date-field') ||
		gfieldClasses.includes('gcldf-field');

	// Not our rule — pass through.
	if (!isCurrentTime && !isCurrentDate && !isTimeField && !isDateField) {
		return isMatch;
	}

	// Pass through GPACL sentinel values — these are handled by advanced-logic.ts,
	// not by us. Without this guard, our filter would intercept the dependency-tracking
	// gpacl_pass rules (which use _gpacl_current_date as fieldId) and return false.
	if (isAnySentinel(rule.value as string)) {
		return isMatch;
	}

	// Pass through rules with empty values on real date/time fields — these are
	// native GF "field is not empty" checks, not date comparison rules.
	// Only the current-date/time sentinels should be evaluated with empty values.
	if (!isCurrentDate && !isCurrentTime && !rule.value) {
		return isMatch;
	}

	const useVisitorTz =
		window[`GPACL_DATE_USE_VISITOR_TIMEZONE_${formId}`] === true ||
		window[`GPCLD_USE_VISITOR_TIMEZONE_${formId}`] === true;

	const serverTzOffsetHours =
		window.GPACLDateConfig?.serverTzOffsetHours ?? 0;

	let fieldValue: number | false = false;
	let ruleValue: number | string = rule.value;

	// ---- Current Time sentinel ----
	if (isCurrentTime) {
		const now = new Date();
		const compareDate = new Date();

		// gpcldBaseDate: anchor the time comparison to a specific date.
		if (rule.gpcldBaseDate !== undefined) {
			const base = rule.gpcldBaseDate;
			if (typeof base === 'number' || !isNaN(Number(base))) {
				compareDate.setDate(compareDate.getDate() + Number(base));
			} else {
				const baseDate = new Date(base as string);
				compareDate.setFullYear(baseDate.getFullYear());
				compareDate.setMonth(baseDate.getMonth());
				compareDate.setDate(baseDate.getDate());
			}
		}

		// Zero seconds so `is`/`isnot` comparisons match at minute
		// granularity — rule values are always HH:MM with no seconds.
		compareDate.setHours(now.getHours(), now.getMinutes(), 0, 0);

		if (useVisitorTz) {
			fieldValue = compareDate.getTime() / 1000;
		} else {
			// Shift both fieldValue and ruleValue into "server wall-clock"
			// space so the comparison is timezone-correct.
			//
			// getTime() returns a UTC epoch. Adding the server offset
			// converts it to a "server local time as epoch" value.
			// The same offset is applied to ruleValue below so both
			// sides are in the same reference frame.
			const serverOffsetSec = serverTzOffsetHours * 3600;
			fieldValue = compareDate.getTime() / 1000 + serverOffsetSec;
		}

		const ruleTimeDate = getDateFromTimeString(rule.value);
		if (ruleTimeDate) {
			if (useVisitorTz) {
				ruleValue = ruleTimeDate.getTime() / 1000;
			} else {
				const serverOffsetSec = serverTzOffsetHours * 3600;
				ruleValue = ruleTimeDate.getTime() / 1000 + serverOffsetSec;
			}
		}
	}

	// ---- Current Date sentinel ----
	else if (isCurrentDate) {
		const now = new Date();
		let year: number, month: number, day: number;

		if (useVisitorTz) {
			year = now.getFullYear();
			month = now.getMonth();
			day = now.getDate();
		} else {
			// Compute today in server timezone.
			const serverOffsetMs = serverTzOffsetHours * 3600 * 1000;
			const serverNow = new Date(now.getTime() + serverOffsetMs);
			year = serverNow.getUTCFullYear();
			month = serverNow.getUTCMonth();
			day = serverNow.getUTCDate();
		}

		// Wildcard handling for current date, e.g. "05/*/*".
		// For `is`/`isnot`, treat it as a pattern match instead of a timestamp comparison.
		const wildcard = parseWildcardDatePattern(rule.value);
		if (wildcard && (rule.operator === 'is' || rule.operator === 'isnot')) {
			const currentMonth = month + 1;
			const currentDay = day;
			const currentYear = year;

			const patternYear =
				wildcard.year !== undefined && wildcard.year < 100
					? currentYear % 100
					: currentYear;

			const matches =
				(wildcard.month === undefined ||
					wildcard.month === currentMonth) &&
				(wildcard.day === undefined || wildcard.day === currentDay) &&
				(wildcard.year === undefined || wildcard.year === patternYear);

			return rule.operator === 'isnot' ? !matches : matches;
		}

		fieldValue = Date.UTC(year, month, day) / 1000;
	}

	// ---- Time field ----
	else if (isTimeField) {
		// GF time fields render three sub-inputs with specific IDs:
		//   #input_{formId}_{fieldId}_1 (hour input)
		//   #input_{formId}_{fieldId}_2 (minute input)
		//   #input_{formId}_{fieldId}_3 (AM/PM select)
		// NOTE: #input_{formId}_{fieldId} is the hour's CONTAINER div, not a parent of all three.
		// The three inputs are spread across sibling divs inside .ginput_complex.
		// Use direct ID selectors for reliability.
		const $hourInput = jQuery(`#input_${formId}_${fieldId}_1`);
		const $minuteInput = jQuery(`#input_${formId}_${fieldId}_2`);
		const $ampmInput = jQuery(`#input_${formId}_${fieldId}_3`);

		const hour = parseInt(($hourInput.val() as string) || '0', 10);
		const minute = parseInt(($minuteInput.val() as string) || '0', 10);
		const ampm = ($ampmInput.val() as string) || '';

		const timeString = `${zeroPad(hour, 2)}:${zeroPad(minute, 2)}${ampm}`;
		const timeDate = getDateFromTimeString(timeString);

		if (!timeDate) {
			fieldValue = false;
		} else {
			// For time field inputs, both fieldValue and ruleValue are created from
			// new Date().setHours() in the browser's local timezone. No TZ adjustment
			// is needed — we're comparing relative times of day, not absolute timestamps.
			// This matches GWCLD's behavior where fieldValue = datetime.getTime()/1000
			// and ruleValue = compareDate.getTime()/1000 (both raw local time).
			fieldValue = timeDate.getTime() / 1000;
		}

		const ruleTimeDate = getDateFromTimeString(rule.value);
		if (ruleTimeDate) {
			ruleValue = ruleTimeDate.getTime() / 1000;
		}
	}

	// ---- Date field ----
	else if (isDateField) {
		// For date fields, the single text/datepicker input has id="input_{formId}_{fieldId}".
		const $sourceInput = $gfield.find(`#input_${formId}_${fieldId}`);
		const fieldFormat = gpGetDateFieldFormat($sourceInput);

		// ---- Wildcard handling ----
		if (rule.value.includes('*')) {
			const dateParts = gpGetDateFieldDate($sourceInput, fieldFormat);
			if (dateParts) {
				const ruleParts = rule.value.split('/');
				const month =
					ruleParts[0] !== '*'
						? parseInt(ruleParts[0], 10)
						: dateParts.month;
				const day =
					ruleParts[1] !== '*'
						? parseInt(ruleParts[1], 10)
						: dateParts.day;
				const year =
					ruleParts[2] !== '*'
						? parseInt(ruleParts[2], 10)
						: dateParts.year;

				fieldValue = gpGetDateFieldTimestamp($sourceInput, formId) as
					| number
					| false;
				ruleValue = Date.UTC(year, month - 1, day) / 1000;
			}
		}

		// ---- Day-of-week tag handling ----
		else if (
			/^\{(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\}$/i.test(
				rule.value
			)
		) {
			const dateObj = gpGetDateFieldDateObject($sourceInput);
			if (dateObj) {
				fieldValue = dateObj.getDay(); // 0=Sunday, 6=Saturday
			}

			const days = [
				'sunday',
				'monday',
				'tuesday',
				'wednesday',
				'thursday',
				'friday',
				'saturday',
			];
			const tagMatch = rule.value.match(/^\{(\w+)\}$/);
			if (tagMatch) {
				ruleValue = days.indexOf(tagMatch[1].toLowerCase());
			}
		}

		// ---- Standard date comparison ----
		else {
			fieldValue = gpGetDateFieldTimestamp($sourceInput, formId);

			// Already a numeric timestamp (pre-resolved)? Use directly.
			if (/^\d{9,}$/.test(rule.value)) {
				ruleValue = Number(rule.value);
			}
			// Parse date string or CSV from field-to-field merge tag resolution.
			else if (
				typeof rule.value === 'string' &&
				rule.value !== '' &&
				!isNegativeUnixTimestamp(rule.value)
			) {
				// Determine which format to use for parsing the rule value.
				// If this came from field-to-field, use the TARGET field's
				// format (the field whose value was resolved). Otherwise
				// use the source field's format (existing behavior for
				// static date strings entered in the admin UI).
				let parseFormat = fieldFormat;
				const resolvedIds = rule.resolvedFieldIds;
				if (resolvedIds && resolvedIds.length === 1) {
					const $targetInput = jQuery(
						`#input_${formId}_${parseInt(resolvedIds[0], 10)}`
					);
					if ($targetInput.length) {
						parseFormat = gpGetDateFieldFormat($targetInput);
					}
				}

				// Normalize CSV from dropdown date fields.
				// GFMergeTag.getMergeTagValue() joins multi-input values
				// with ", ". CSV order matches the target field's DOM
				// order which follows its date format.
				const csvMatch = rule.value.match(
					/^(\d{1,4}),\s*(\d{1,2}),\s*(\d{1,4})$/
				);
				if (csvMatch) {
					const normalized = [
						csvMatch[1],
						csvMatch[2],
						csvMatch[3],
					].join(parseFormat.sep);
					const ruleDate = getDateFromDateString(
						normalized,
						parseFormat
					);
					if (ruleDate) {
						ruleValue = ruleDate.getTime() / 1000;
					}
				}
				// Standard date string with separator.
				else if (
					rule.value.includes('/') ||
					rule.value.includes('-') ||
					rule.value.includes('.')
				) {
					const ruleDate = getDateFromDateString(
						rule.value,
						parseFormat
					);
					if (ruleDate) {
						ruleValue = ruleDate.getTime() / 1000;
					}
				}
			}
		}
	}

	// ---- gpcld_require_date_selection JS filter ----
	// GF doesn't have a JS filter system for this, so we apply the default behavior:
	// if fieldValue is false (no date selected), adjust based on operator.
	if (fieldValue === false) {
		if (rule.operator === 'isnot') {
			// isnot with no value → always false (field is empty, not "not equal").
			fieldValue = typeof ruleValue === 'number' ? ruleValue : 0;
		} else if (rule.operator === '>') {
			fieldValue = typeof ruleValue === 'number' ? ruleValue - 1 : 0;
		} else {
			ruleValue = '';
		}
	}

	return gf_matches_operation(
		String(fieldValue),
		String(ruleValue),
		rule.operator
	);
}

// -------------------------------------------------------------------------
// Module initialization
// -------------------------------------------------------------------------

// Register with the module registry on import.
// Date Logic handles date/time field rules and current-date/time sentinels.
// It defers to Advanced Logic for GPACL sentinels (checked inside evaluateDateRule).
registerModule({
	slug: 'date-logic',
	handles: (rule, formId) => {
		// Quick check: if it's a GPACL sentinel, we don't handle it.
		// Advanced Logic handles those.
		if (isAnySentinel(rule.value as string)) {
			return false;
		}

		// Check if this is a current-date/time sentinel field ID.
		const fieldId = String(rule.fieldId);
		if (
			fieldId === '_gpacl_current_date' ||
			fieldId === '_gpcld_current_date' ||
			fieldId === '_gpacl_current_time' ||
			fieldId === '_gpcld_current_time'
		) {
			return true;
		}

		// Check if the source field is a date or time field by CSS class.
		// GF renders field containers as #field_{formId}_{fieldId}.
		const $gfield = jQuery(`#field_${formId}_${fieldId}`);
		if (!$gfield.length) {
			return false;
		}
		const classes = $gfield.attr('class') || '';
		return (
			classes.includes('gpacl-date-field') ||
			classes.includes('gcldf-field')
		);
	},
	evaluate: (isMatch, formId, rule) =>
		evaluateDateRule(isMatch, formId, rule as GPACLRule),
});
